// src/context/ModalContext.js
import React, { createContext, useState, useRef } from 'react'; // ← Añadido useRef para generar IDs únicos

// Creamos el contexto
export const ModalContext = createContext();

// ModalProvider: provee la funcionalidad de manejo de modales
export const ModalProvider = ({ children }) => {
  // Ref para llevar un contador de IDs únicos para cada modal
  const idCounter = useRef(0); // NUEVO: contador incremental para identificadores únicos

  // Creamos un estado para almacenar las pilas independientes por pestaña.
  // Inicialmente definimos dos pestañas: "home" y "likes".
  const [modalStacks, setModalStacks] = useState({
    home: [],
    likes: []
  });

  // Función para abrir un modal.
  // Se espera recibir el contenido del modal y un objeto de opciones.
  // Entre las opciones se incluye "tab" para determinar en qué pestaña se debe agregar.
  const openModal = (modalContent, options = {}) => {
    const tab = options.tab || 'home';

    // NUEVO: generar un ID único para este modal
    idCounter.current += 1;
    const modalId = idCounter.current;

    setModalStacks(prev => {
      const stackForTab = prev[tab] || [];
      const newModal = {
        id: modalId,
        content: modalContent,
        metadata: options.metadata || {},
        overlayClassName: options.overlayClassName || undefined,
        disableSwipe: options.disableSwipe || false
      };
      return {
        ...prev,
        [tab]: [...stackForTab, newModal]
      };
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // `updateModal` ahora acepta:
  //   - Primero, `newContent` (si queremos cambiar content; si es `null`, se conserva).
  //   - Segundo, un objeto de “opciones” que puede incluir `metadata` parcial.
  const updateModal = (newContent = null, options = {}) => {
    // `options.tab` es la pestaña en la que modificar; por defecto 'home'
    const tab = options.tab || 'home';
    // `options.metadata` es un objeto con claves/metadatos que queremos fusionar
    const newMetadataPartial = options.metadata || {};

    setModalStacks(prev => {
      const stackForTab = prev[tab] || [];
      if (stackForTab.length === 0) return prev;

      // Tomamos el modal que vamos a actualizar (el último de la pila)
      const lastIndex = stackForTab.length - 1;
      const lastModal = stackForTab[lastIndex];

      // Fusionamos el contenido (si nos pasan `newContent`) o lo preservamos
      const updatedContent =
        newContent !== null ? newContent : lastModal.content;

      // Y fusionamos la metadata antigua con la nueva
      const updatedMetadata = {
        ...lastModal.metadata,
        ...newMetadataPartial
      };

      const updatedModal = {
        ...lastModal,
        content: updatedContent,
        metadata: updatedMetadata
      };

      return {
        ...prev,
        [tab]: [
          ...stackForTab.slice(0, lastIndex),
          updatedModal
        ]
      };
    });
  };

  // Función para cerrar un modal.
  // Se puede pasar la pestaña en la que se desea cerrar (por defecto "home").
  const closeModal = (tab) => {
    const targetTab = tab || 'home';
    setModalStacks(prevStacks => {
      // Obtenemos la pila de la pestaña actual.
      const stackForTab = prevStacks[targetTab] || [];
      // Retornamos el objeto con la pila actualizada (eliminando el último modal).
      return {
        ...prevStacks,
        [targetTab]: stackForTab.slice(0, -1)
      };
    });
  };

  return (
    <ModalContext.Provider value={{ modalStacks, openModal, closeModal, updateModal }}>
      {children}
    </ModalContext.Provider>
  );
};
